import { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BaseChannel, User } from "@sendbird/chat";
import { GroupChannel, GroupChannelCollection, GroupChannelCollectionEventHandler, GroupChannelEventContext, GroupChannelFilter, GroupChannelListOrder, MessageCollection, MessageCollectionEventHandler, MessageCollectionInitPolicy, MessageFilter, UnreadChannelFilter, GroupChannelListQuery, GroupChannelListQueryParams, QueryType } from "@sendbird/chat/groupChannel";
import { UserMessage } from "@sendbird/chat/message";
import { useRouter } from "next/navigation";
import { useSoftLoginDataFromLocalStorage } from "hooks/useSoftLoginDataFromLocalStorage";
import { createChannel, sendbirdChat } from "services/sendbird/sendbird";
import { selectUser } from "stores/auth/authSlice";
import { useAppSelector } from "stores/hooks";
import { formatDateRange } from "utils/dates";
import { canUseDOM } from "utils/dom";
type SendbirdProviderProps = {
  children: ReactNode;
};
interface CreateChannelArgs {
  channelName: string;
  userIdsToInvite: string[];
  message?: string;
  coverImg?: "";
  adultNumber?: number;
  childNumber?: number;
  startDate?: string | null;
  endDate?: string | null;
  pet?: boolean;
  petNumber?: number;
  dayCount?: number;
  hostProfileName?: string;
  hostProfileUrl?: string;
  fromBooking?: boolean;
  propertyCity?: string;
  propertyState?: string;
  pmcId?: string;
  pmsSystem?: string;
  propertyId?: string;
  name?: string;
  mobile_number?: string;
  email?: string;
  dialer_code?: string;
  host_id?: string;
  traveler_id?: string;
  redirectToChannel?: boolean;
}
interface LoadMessagesParams {
  channel: GroupChannel;
  messageHandler: MessageCollectionEventHandler;
  onCacheResult: (err: Error | null, messages: UserMessage[] | null) => void;
  onApiResult: (err: Error | null, messages: UserMessage[] | null) => void;
}
interface SendbirdDataState {
  isAlreadyExistingChannel: boolean;
  sendBirdUser: User | null;
  loading: boolean;
  connectedToChat: boolean;
  groupChannelCollection: GroupChannelCollection | null;
  channels: GroupChannel[] | null;
  currentlyJoinedChannel: GroupChannel | null;
  messagesLoading: boolean;
  messageCollection: MessageCollection | null;
  messages: UserMessage[];
  unreadCount: number;
  erroMessage: boolean;
}
interface LoadChannelsArgs {
  channelNameContainsFilter?: string;
  unreadChannelFilter?: boolean;
  messageContainsFilter?: string;
}
interface SendbirdContextState extends SendbirdDataState {
  loadChannels: ({
    channelNameContainsFilter,
    unreadChannelFilter
  }: LoadChannelsArgs) => void;
  handleOpenChannel: (channelUrl: string) => void;
  sendMessage: (messageValue: string) => void;
  startTyping: () => void;
  endTyping: () => void;
  resetCurrentlyJoinedChannel: () => void;
  handleCreateChannel: (CreateChannelArgs) => Promise<string | Error | null>;
}
export const SendbirdContext = createContext<SendbirdContextState | undefined>(undefined);
const SendbirdProvider = (props: SendbirdProviderProps) => {
  const {
    children
  } = props;
  const [state, setState] = useState<SendbirdDataState>({
    sendBirdUser: null,
    loading: false,
    connectedToChat: false,
    groupChannelCollection: null,
    channels: null,
    currentlyJoinedChannel: null,
    messagesLoading: false,
    messageCollection: null,
    messages: [],
    isAlreadyExistingChannel: false,
    unreadCount: 0,
    erroMessage: false
  });

  // need to access state in message received callback
  const stateRef = useRef<SendbirdDataState>(state);
  stateRef.current = state;
  const router = useRouter();
  const currentUser = useAppSelector(selectUser);
  const {
    softLoginData
  } = useSoftLoginDataFromLocalStorage();
  const channelsRef = useRef<GroupChannel[]>([]);
  channelsRef.current = state.channels || [];
  const handleUnreadMsg = async () => {
    const unreadCount = await sendbirdChat.groupChannel.getTotalUnreadMessageCount();
    setState(prevState => ({
      ...prevState,
      unreadCount: unreadCount || 0
    }));
  };
  const loadChannels = useCallback(({
    channelNameContainsFilter = "",
    unreadChannelFilter = false
  }: LoadChannelsArgs) => {
    setState(prevState => ({
      ...prevState,
      loading: true
    }));
    const channelHandlers: GroupChannelCollectionEventHandler = {
      onChannelsAdded: (_context: GroupChannelEventContext, channels: BaseChannel[]) => {
        const updatedChannels = [...channels, ...(channelsRef.current || [])];
        setState(prevState => ({
          ...prevState,
          channels: (updatedChannels as GroupChannel[])
        }));
      },
      onChannelsDeleted: (_context: GroupChannelEventContext, channelUrls: string[]) => {
        const updatedChannels = channelsRef?.current?.filter(channel => !channelUrls.includes(channel.url));
        setState(prevState => ({
          ...prevState,
          channels: updatedChannels
        }));
      },
      onChannelsUpdated: (_context: GroupChannelEventContext, channels: BaseChannel[]) => {
        const updatedChannels = channelsRef?.current?.map(channel => {
          const updatedChannel = channels.find(incomingChannel => incomingChannel.url === channel.url);
          if (updatedChannel) {
            return updatedChannel;
          }
          return channel;
        });
        void handleUnreadMsg();
        setState(prevState => ({
          ...prevState,
          channels: (updatedChannels as GroupChannel[])
        }));
      }
    };
    const groupChannelFilter = new GroupChannelFilter();
    groupChannelFilter.includeEmpty = true;
    groupChannelFilter.channelNameContainsFilter = channelNameContainsFilter;
    if (unreadChannelFilter) {
      groupChannelFilter.unreadChannelFilter = UnreadChannelFilter.UNREAD_MESSAGE;
    }
    const collection = sendbirdChat.groupChannel.createGroupChannelCollection({
      filter: groupChannelFilter,
      order: GroupChannelListOrder.LATEST_LAST_MESSAGE
    });
    collection.setGroupChannelCollectionHandler(channelHandlers);
    collection.loadMore().then(channels => {
      // Filter channels based on message content

      setState(prevState => ({
        ...prevState,
        channels,
        loading: false
      }));
      return true;
    }).catch(error => {
      console.log(error);
      setState(prevState => ({
        ...prevState,
        loading: false
      }));
    });
  }, []);
  useEffect(() => {
    const userId = currentUser?.userId || softLoginData?.userId;
    if (userId) {
      sendbirdChat.connect(userId.toString() || "").then(async user => {
        if (currentUser?.userId) {
          const unreadCount = await sendbirdChat.groupChannel.getTotalUnreadMessageCount();
          setState(prevState => ({
            ...prevState,
            sendBirdUser: user,
            connectedToChat: true,
            unreadCount: unreadCount || 0
          }));
          loadChannels({});
          await sendbirdChat.updateCurrentUserInfo({
            nickname: currentUser?.firstName || ""
          });
        }

        // Fetch user metadata
        const existingMetadata = (user.metaData as {
          user_type?: string;
        });
        if (!existingMetadata?.user_type) {
          // Metadata does not exist, create it
          await user.createMetaData({
            user_type: "TRAVELER"
          });
        }
        return true;
      }).catch(error => {
        console.log("connection err: ", error);
      });
    }
  }, [currentUser?.firstName, currentUser?.userId, softLoginData?.userId]);
  const loadMessages = useCallback(({
    channel,
    messageHandler,
    onCacheResult,
    onApiResult
  }: LoadMessagesParams) => {
    const messageFilter = new MessageFilter();
    const collection = channel.createMessageCollection({
      filter: messageFilter,
      startingPoint: Date.now(),
      prevResultLimit: 50
    });
    collection.setMessageCollectionHandler(messageHandler);
    collection.initialize(MessageCollectionInitPolicy.CACHE_AND_REPLACE_BY_API).onCacheResult((err, messages) => {
      if (messages) {
        const allMessages = [...messages, ...collection.failedMessages].slice().reverse();
        onCacheResult(err, (allMessages as UserMessage[]));
      }
    }).onApiResult((err, messages) => {
      if (messages) {
        const allMessages = [...messages, ...collection.failedMessages].slice().reverse();
        onApiResult(err, (allMessages as UserMessage[]));
      }
    });
    return collection;
  }, []);
  const handleOpenChannel = useCallback((channelUrl: string) => {
    if (state.messageCollection && state.messageCollection.dispose) {
      state.messageCollection?.dispose();
    }

    // if (state.currentlyJoinedChannel?.url === channelUrl) {
    //   return null
    // }

    const {
      channels
    } = state;
    setState({
      ...state,
      messagesLoading: true
    });
    const channel = channels?.find(ch => ch.url === channelUrl);
    if (!channel) return null;
    channel.markAsRead().then(() => true).catch(error => console.log(error));
    const onCacheResult = (_err: Error | null, messages: UserMessage[] | null) => {
      if (messages) {
        const reversedMessages = messages.slice().reverse();
        const sortMessages = (msg: UserMessage[]) => msg.sort((a, b) => a.createdAt - b.createdAt);
        setState(prevState => ({
          ...(stateRef.current ? stateRef.current : prevState),
          currentlyJoinedChannel: channel,
          messages: sortMessages(reversedMessages),
          messagesLoading: false
        }));
      }
    };
    const onApiResult = (_err: Error | null, messages: UserMessage[] | null) => {
      if (messages) {
        const reversedMessages = messages.slice().reverse();
        const sortMessages = (msg: UserMessage[]) => msg.sort((a, b) => a.createdAt - b.createdAt);
        setState(prevState => ({
          ...(stateRef.current ? stateRef.current : prevState),
          currentlyJoinedChannel: channel,
          messages: sortMessages(reversedMessages),
          messagesLoading: false
        }));
      }
    };
    const messageHandlers: MessageCollectionEventHandler = {
      onChannelUpdated: (_, _channel) => {
        // Notifies when the channel has been updated.
        setState(prev => prev ? {
          ...prev,
          _channel
        } : prev);
      },
      onMessagesUpdated: (_context, _channel, messages) => {
        // Mark the channel as read (similar to your reference code)
        void stateRef.current.currentlyJoinedChannel?.markAsRead();

        // Create a new array to hold the updated messages
        const updatedMessages = [...stateRef.current.messages];

        // Iterate through the incoming messages
        // eslint-disable-next-line @typescript-eslint/no-for-in-array
        for (const i in messages) {
          const incomingMessage = (messages[i] as UserMessage);

          // Find the index of the existing message in the state using `reqId`
          const indexOfExisting = stateRef.current.messages.findIndex(message => {
            return incomingMessage.reqId === message.reqId;
          });

          // If we find an existing message with the same `reqId`, replace it with the updated one
          if (indexOfExisting !== -1) {
            updatedMessages[indexOfExisting] = incomingMessage;
          }

          // If the incoming message doesn't have a `reqId`, treat it as a new message and add it to the list
          if (!incomingMessage.reqId) {
            updatedMessages.push(incomingMessage);
          }
        }

        // Update the state with the updated messages array
        setState({
          ...stateRef.current,
          messages: updatedMessages
        });
      },
      onMessagesAdded: (_context, _channel, messages) => {
        const updatedMessages = [...stateRef.current.messages, ...messages];
        setState({
          ...stateRef.current,
          messages: (updatedMessages as UserMessage[])
        });
      }
    };
    const collection = loadMessages({
      channel,
      messageHandler: messageHandlers,
      onCacheResult,
      onApiResult
    });
    setState(prevState => ({
      ...prevState,
      messageCollection: collection
    }));
    return null;
  }, [loadMessages, state]);
  const sendMessage = useCallback((messageValue: string) => {
    if (!state.currentlyJoinedChannel) {
      return null;
    }
    state.currentlyJoinedChannel.sendUserMessage({
      message: messageValue
    }).onSucceeded(() => {
      stateRef.current.currentlyJoinedChannel?.markAsRead().then(() => true).catch(error => console.log(error));
      setState(prevState => ({
        ...prevState,
        erroMessage: false
      }));

      // Refresh messages after sending the message
      // refreshMessages()
    }).onFailed(error => {
      console.log(error);
    });
    return null;
  }, [state.currentlyJoinedChannel]);
  const handleCreateChannel = useCallback(async ({
    channelName,
    userIdsToInvite,
    message = "",
    coverImg = "",
    adultNumber = 1,
    childNumber = 0,
    startDate = null,
    endDate = null,
    pet = false,
    petNumber = 0,
    dayCount = 0,
    hostProfileName = "",
    hostProfileUrl = "",
    fromBooking = false,
    propertyCity = "",
    propertyState = "",
    pmcId = "",
    pmsSystem = "",
    propertyId = "",
    name = "",
    mobile_number = "",
    email = "",
    dialer_code = "",
    host_id = "",
    traveler_id = "",
    redirectToChannel = true
  }: CreateChannelArgs): Promise<string | null> => {
    // Explicitly return Promise<string | null>
    try {
      let alreadyExistChannelUrl = "";
      let channel: GroupChannel;
      const params: GroupChannelListQueryParams = {
        userIdsFilter: {
          userIds: userIdsToInvite,
          includeMode: true,
          queryType: QueryType.AND
        }
      };
      const query: GroupChannelListQuery = sendbirdChat.groupChannel.createMyGroupChannelListQuery(params);
      const channels: GroupChannel[] = await query.next();

      // Loop through channels and fetch metadata
      for (const channelObj of channels) {
        const metaData = await channelObj.getMetaData(["propertyId"]); // Fetch metadata

        if (metaData?.propertyId === propertyId) {
          alreadyExistChannelUrl = channelObj.url;
        }
      }
      if (alreadyExistChannelUrl) {
        setState(prevState => ({
          ...prevState,
          isAlreadyExistingChannel: true
        }));
        channel = await sendbirdChat.groupChannel.getChannel(alreadyExistChannelUrl);
        await channel.getMetaData(["status"]);
      } else {
        let isgoogle = "";
        if (canUseDOM()) {
          const search = window.location.search;
          const searchParams = new URLSearchParams(search);
          isgoogle = (searchParams.get("isgoogle") as string);
        }
        channel = await createChannel(channelName, userIdsToInvite, propertyId, pmcId, coverImg, name, mobile_number, email, dialer_code, host_id, traveler_id);
        await channel.createMetaData({
          user_type: "TRAVELER",
          status: "inquiry",
          adultCount: (adultNumber ?? 1).toString(),
          childCount: (childNumber ?? 0).toString(),
          startDate: startDate ?? "",
          endDate: endDate ?? "",
          pet: petNumber && petNumber > 0 ? "true" : pet ? "true" : "false",
          petNumber: (petNumber ?? 0).toString(),
          dayCount: (dayCount ?? "").toString(),
          propertyId: propertyId,
          coverImg: coverImg ?? "",
          propertyCity: propertyCity,
          propertyState: propertyState,
          pmcId: pmcId,
          pmsSystem: pmsSystem,
          hostName: hostProfileName ?? "",
          hostProfileUrl: hostProfileUrl ?? "",
          isgoogle: isgoogle ?? "false"
        });
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (alreadyExistChannelUrl && !fromBooking) {
        await channel.updateMetaData({
          user_type: "TRAVELER",
          status: "inquiry",
          adultCount: (adultNumber ?? 1).toString(),
          childCount: (childNumber ?? 0).toString(),
          startDate: startDate ?? "",
          endDate: endDate ?? "",
          pet: petNumber && petNumber > 0 ? "true" : pet ? "true" : "false",
          petNumber: (petNumber ?? 0).toString(),
          dayCount: (dayCount ?? "").toString(),
          propertyId: propertyId,
          coverImg: coverImg ?? "",
          propertyCity: propertyCity,
          propertyState: propertyState,
          pmcId: pmcId,
          pmsSystem: pmsSystem
        });
        //TODO set message page url or new state for channel url
      }
      if (!alreadyExistChannelUrl) {
        const dateRange = startDate && endDate ? formatDateRange(startDate, endDate) : "";
        channel.sendUserMessage({
          message: `Your inquiry for ${adultNumber} ${adultNumber && adultNumber > 1 ? "guests" : "guest"} on ${dateRange} has been sent.`,
          customType: "initial_inquiry_msg"
        });
      }
      channel.sendUserMessage({
        message: message || ""
      }).onSucceeded(() => {
        console.log("Message sent successfully");
        stateRef.current.currentlyJoinedChannel?.markAsRead().then(() => true).catch(error => console.log(error));
        if (redirectToChannel) {
          void router.push("/messages?channelUrl=" + encodeURIComponent(channel?.url));
        }
      }).onFailed(error => {
        console.error("Failed to send message:", error);
        throw error;
      });
      return channel.url; // ✅ Make sure it returns a string (channel URL)
    } catch (error) {
      console.error("Error creating channel:", error);
      return null;
    }
  }, []);
  const startTyping = useCallback(() => {
    if (state.currentlyJoinedChannel) {
      // eslint-disable-next-line no-void
      void state.currentlyJoinedChannel.startTyping();
    }
  }, [state.currentlyJoinedChannel]);
  const endTyping = useCallback(() => {
    if (state.currentlyJoinedChannel) {
      // eslint-disable-next-line no-void
      void state.currentlyJoinedChannel.endTyping();
    }
  }, [state.currentlyJoinedChannel]);
  const resetCurrentlyJoinedChannel = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      currentlyJoinedChannel: null
    }));
  }, []);

  // Memoize the value object
  const providerValue = useMemo(() => ({
    ...state,
    loadChannels,
    handleOpenChannel,
    sendMessage,
    handleCreateChannel,
    startTyping,
    endTyping,
    resetCurrentlyJoinedChannel
  }), [state, loadChannels, handleOpenChannel, sendMessage, handleCreateChannel, startTyping, endTyping, resetCurrentlyJoinedChannel]);
  return <SendbirdContext.Provider value={providerValue} data-sentry-element="SendbirdContext.Provider" data-sentry-component="SendbirdProvider" data-sentry-source-file="SendBirdProvider.tsx">
      {children}
    </SendbirdContext.Provider>;
};
export default SendbirdProvider;